import React, { useState } from "react";
import { CiLocationOn } from "react-icons/ci";
import { MdEmail } from "react-icons/md";
import { FaPhone } from "react-icons/fa6";
import { FaWhatsapp } from "react-icons/fa";
import { motion } from "framer-motion";
import image11 from "../data/other/11thimg.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay },
  }),
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
    policy: false,
  });

  const [statusMsg, setStatusMsg] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Build WhatsApp message (human readable)
  const buildWhatsAppMessage = () => {
    return (
      `New Contact Form Submission:\n\n` +
      `👤 Name: ${formData.firstName || "-"} ${formData.lastName || "-"}\n` +
      `📧 Email: ${formData.email || "-"}\n` +
      `📝 Subject: ${formData.subject || "-"}\n` +
      `💬 Message: ${formData.message || "-"}`
    );
  };

  // Open wa.me link in a new tab/window
  const openWhatsApp = (numberWithCountryCode, message) => {
    const encoded = encodeURIComponent(message);
    const url = `https://wa.me/${numberWithCountryCode}?text=${encoded}`;
    window.open(url, "_blank");
  };

  // WhatsApp number (change if needed). Use country code + number (no + or dashes).
  const whatsappNumber = "919211413808"; // example: 91 + 9211413808

  // Called when user clicks WhatsApp button or submits form
  const handleWhatsAppClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Basic validation
    if (!formData.firstName && !formData.lastName) {
      setStatusMsg("❗ कृपया अपना नाम दर्ज करें।");
      return;
    }
    if (!formData.email) {
      setStatusMsg("❗ कृपया ईमेल दर्ज करें।");
      return;
    }
    if (!formData.subject) {
      setStatusMsg("❗ कृपया सब्जेक्ट डालें।");
      return;
    }
    if (!formData.message) {
      setStatusMsg("❗ कृपया संदेश लिखें।");
      return;
    }
    if (!formData.policy) {
      setStatusMsg("❗ कृपया privacy policy स्वीकार करें।");
      return;
    }

    setStatusMsg("Opening WhatsApp...");

    const msg = buildWhatsAppMessage();
    openWhatsApp(whatsappNumber, msg);

    // Optional: keep form values or clear after opening WhatsApp. 
    // If you want to clear, uncomment below:
    // setFormData({ firstName: "", lastName: "", email: "", subject: "", message: "", policy: false });
  };

  return (
    <div className="overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50 to-blue-100 min-h-screen relative">
      {/* HERO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="w-full min-h-[300px] flex justify-center items-center bg-gradient-to-br from-slate-800 to-slate-950 px-4 relative"
      >
        <motion.div initial="hidden" whileInView="visible" variants={fadeInUp} className="text-center max-w-lg p-6 z-10">
          <h1 className="text-indigo-300 text-xl font-semibold uppercase">Contact Us</h1>
          <h2 className="text-white text-4xl font-extrabold mt-4">Get in Touch With Us Today</h2>
          <p className="text-slate-300 mt-4">Have any inquiries? We're here to help.</p>
        </motion.div>
      </motion.div>

      {/* MAIN SECTION */}
      <div className="w-full flex flex-col md:flex-row justify-center bg-white p-10 mx-6 rounded-3xl -mt-10 gap-10 shadow-2xl">
        {/* FORM */}
        <motion.div initial={{ opacity: 0, x: -80 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="w-full md:w-1/2">
          <h1 className="font-extrabold text-4xl text-blue-900">We'd Love to Help</h1>
          <p className="text-gray-700 text-lg mb-6">Our friendly team would love to hear from you.</p>

          {/* onSubmit now also triggers WhatsApp */}
          <form className="flex flex-col gap-y-4" onSubmit={handleWhatsAppClick}>
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="rounded-3xl p-4 w-full border border-gray-300"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="rounded-3xl p-4 w-full border border-gray-300"
              />
            </div>

            <input
              type="email"
              name="email"
              placeholder="Your Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="rounded-3xl p-4 w-full border border-gray-300"
            />

            <input
              type="text"
              name="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="rounded-3xl p-4 w-full border border-gray-300"
            />

            <textarea
              name="message"
              placeholder="Leave us a message..."
              value={formData.message}
              onChange={handleChange}
              required
              className="rounded-3xl p-4 w-full h-40 border border-gray-300 resize-none"
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="policy"
                checked={formData.policy}
                onChange={handleChange}
                required
                className="w-5 h-5"
              />
              <label className="text-gray-700">You agree to our friendly <span className="text-blue-800 underline">privacy policy</span></label>
            </div>

            {/* ONLY WhatsApp button - no EmailJS or Send Message button */}
            <div className="flex items-center gap-4 mt-4">
              <button
                type="submit"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 p-4 rounded-full text-white font-bold w-full sm:w-1/2 shadow-lg"
              >
                <FaWhatsapp className="text-lg" /> Send via WhatsApp
              </button>

              {/* keep an optional small reset/clear if you want (not required) */}
              <button
                type="button"
                onClick={() =>
                  setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    subject: "",
                    message: "",
                    policy: false,
                  })
                }
                className="bg-gray-200 hover:bg-gray-300 p-3 rounded-full text-gray-800 w-full sm:w-1/2"
              >
                Clear
              </button>
            </div>

            {statusMsg && <p className="font-semibold mt-2">{statusMsg}</p>}
          </form>
        </motion.div>

        {/* IMAGE */}
        <motion.div initial={{ opacity: 0, x: 80 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="w-full md:w-1/2 flex justify-center">
          <motion.img src={image11} alt="Contact" whileHover={{ scale: 1.05 }} className="w-full max-w-md rounded-3xl shadow-lg" />
        </motion.div>
      </div>

      {/* CONTACT INFO CARDS */}
      <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 mt-20 text-center px-10 mb-16">
        {[
          { Icon: CiLocationOn, title: "Our Location", desc: "C-115A, Mohan Garden, New Delhi" },
          { Icon: MdEmail, title: "Email", desc: "mdcontrolanditsolutions@gmail.com" },
          { Icon: FaPhone, title: "Phone", desc: "+91 9211413808 • +91 8851738899" },
        ].map(({ Icon, title, desc }, i) => (
          <motion.div key={i} whileHover={{ y: -8, scale: 1.03 }} className="flex flex-col items-center bg-gradient-to-br from-blue-100 to-blue-50 p-6 rounded-2xl shadow-md">
            <Icon className="text-white text-4xl bg-blue-700 rounded-2xl p-3 mb-3" />
            <h1 className="font-bold text-lg text-blue-900">{title}</h1>
            <p className="text-sm text-gray-700 mt-2">{desc}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* FLOATING WHATSAPP ICON */}
      <button
        onClick={handleWhatsAppClick}
        aria-label="Send form via WhatsApp"
        className="fixed right-6 bottom-6 bg-green-600 hover:bg-green-700 text-white p-4 rounded-full shadow-xl z-50 flex items-center justify-center"
      >
        <FaWhatsapp className="text-2xl" />
      </button>
    </div>
  );
};

export default ContactUs;
